import React from 'react';
import { Link } from 'react-router-dom';
import { useSessionContext } from '../context/SessionContext';
import { useGamificationContext } from '../context/GamificationContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Button from '../components/ui/Button';
import { FiPlus, FiClock, FiTarget, FiTrendingUp, FiAward, FiActivity, FiFileText, FiStar } from 'react-icons/fi';
import { formatDate, formatDuration } from '../lib/utils';

const Dashboard = () => {
  const { sessions } = useSessionContext();
  const { userStats, recentUnlocks, gamificationEnabled } = useGamificationContext();

  const recentSessions = sessions.slice(0, 3);

  const stats = {
    totalSessions: sessions.length,
    totalMatTime: sessions.reduce((sum, s) => sum + (s.duration || 0), 0),
    techniquesLearned: sessions.reduce((sum, s) => sum + (s.techniques?.length || 0), 0),
    winRate: sessions.reduce((sum, s) => sum + (s.wins || 0), 0) + sessions.reduce((sum, s) => sum + (s.losses || 0), 0) > 0
      ? Math.round((sessions.reduce((sum, s) => sum + (s.wins || 0), 0) / (sessions.reduce((sum, s) => sum + (s.wins || 0), 0) + sessions.reduce((sum, s) => sum + (s.losses || 0), 0))) * 100)
      : 0
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">
          Dashboard
        </h1>
        <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400">
          Track your progress and stay sharp on the mats.
        </p>
      </div>

      {/* Gamification Status Card */}
      {gamificationEnabled && (
        <div className="mb-6">
          <Link to="/achievements">
            <Card className="bg-gradient-to-r from-blue-900 to-blue-800 dark:from-slate-800 dark:to-slate-900 text-white border-none shadow-lg hover:shadow-xl transition-all">
              <CardContent className="p-4 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-blue-700 dark:bg-slate-700 flex items-center justify-center border-2 border-blue-400 dark:border-slate-500">
                    <span className="text-xl sm:text-2xl font-bold">{userStats.level}</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">Level {userStats.level} Grappler</h3>
                    <div className="flex items-center gap-2 text-blue-200 dark:text-slate-300 text-sm">
                      <FiStar className="w-4 h-4 text-yellow-400 fill-current" />
                      <span>{userStats.xp} XP Earned</span>
                    </div>
                    <div className="w-32 sm:w-48 bg-blue-950 dark:bg-slate-950 rounded-full h-2 mt-2">
                      <div className="bg-yellow-400 h-2 rounded-full transition-all" style={{ width: `${userStats.progressToNext}%` }} />
                    </div>
                  </div>
                </div>
                {recentUnlocks.length > 0 && (
                  <div className="flex flex-col items-end text-right hidden sm:flex">
                    <span className="text-xs text-blue-300 dark:text-slate-400 uppercase tracking-wider mb-1">Latest Achievement</span>
                    <div className="flex items-center gap-2 bg-blue-800/50 dark:bg-slate-700/50 px-3 py-1.5 rounded-lg border border-blue-700 dark:border-slate-600">
                      <FiAward className="text-yellow-400" />
                      <span className="font-medium text-sm">{recentUnlocks[recentUnlocks.length - 1].title}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <Link to="/session" className="block">
          <Card className="hover:shadow-md transition-shadow cursor-pointer border-yellow-200 dark:border-yellow-900/50 hover:border-yellow-300 dark:hover:border-yellow-700 h-full">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center space-x-3">
                <div className="bg-yellow-100 dark:bg-yellow-900/40 p-2 sm:p-3 rounded-lg flex-shrink-0">
                  <FiPlus className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-sm sm:text-base">
                    Log Session
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    Record your latest training
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/techniques" className="block">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 dark:bg-blue-900/40 p-2 sm:p-3 rounded-lg flex-shrink-0">
                  <FiTarget className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-sm sm:text-base">
                    Technique Library
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    Browse and study techniques
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/analytics" className="block">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center space-x-3">
                <div className="bg-green-100 dark:bg-green-900/40 p-2 sm:p-3 rounded-lg flex-shrink-0">
                  <FiTrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-sm sm:text-base">
                    View Analytics
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    Analyze your progress
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/weekly-summary" className="block">
          <Card className="hover:shadow-md transition-shadow cursor-pointer border-purple-200 dark:border-purple-900/50 hover:border-purple-300 dark:hover:border-purple-700 h-full">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center space-x-3">
                <div className="bg-purple-100 dark:bg-purple-900/40 p-2 sm:p-3 rounded-lg flex-shrink-0">
                  <FiFileText className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-sm sm:text-base">
                    Weekly Summary
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    Review your week
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Sessions</p>
                <FiActivity className="w-5 h-5 sm:w-8 sm:h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">
                {stats.totalSessions}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Mat Time</p>
                <FiClock className="w-5 h-5 sm:w-8 sm:h-8 text-green-600 dark:text-green-400" />
              </div>
              <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">
                {formatDuration(stats.totalMatTime)}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Techniques</p>
                <FiTarget className="w-5 h-5 sm:w-8 sm:h-8 text-purple-600 dark:text-purple-400" />
              </div>
              <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">
                {stats.techniquesLearned}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Win Rate</p>
                <FiAward className="w-5 h-5 sm:w-8 sm:h-8 text-yellow-600 dark:text-yellow-400" />
              </div>
              <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">
                {stats.winRate}%
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-base sm:text-lg">
              Recent Sessions
              <Link to="/sessions">
                <Button variant="ghost" size="sm" className="text-xs sm:text-sm">
                  View All
                </Button>
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentSessions.length > 0 ? (
              <div className="space-y-3 sm:space-y-4">
                {recentSessions.map((session) => (
                  <Link 
                    key={session.id} 
                    to={`/sessions/${session.id}/edit`}
                    className="block"
                  >
                    <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-100 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900 dark:text-slate-100 text-sm sm:text-base truncate">
                          {session.partner || session.sessionType}
                        </p>
                        <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                          {formatDate(session.date)} • {session.sessionType}
                        </p>
                      </div>
                      <div className="text-right ml-2 flex-shrink-0">
                        <p className="text-xs sm:text-sm font-medium text-slate-900 dark:text-slate-100">
                          {formatDuration(session.duration)}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {session.techniques?.length || 0} techniques
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                <FiActivity className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm sm:text-base">No sessions logged yet</p>
                <Link to="/session">
                  <Button variant="outline" size="sm" className="mt-2 text-xs sm:text-sm">
                    Log Your First Session
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;